<template>
  <div id="ct-container" ref="ctElement">
    <div class="c-cybersecurity-tools">
      <div class="mobile-instruction" v-if="!isLoading && fields && fields.mobileInstruction">
        <sc-text v-if="fields && fields.mobileInstruction" :field="fields.mobileInstruction" />
      </div>
      <div class="table-label" v-if="!isLoading && fields && fields.tableLabel">
        <sc-text v-if="fields && fields.tableLabel" :field="fields.tableLabel" />
      </div>
      <div v-if="!isLoading" class="scrollable" ref="scElement">
        <table>
          <tbody>
            <tr class="column-headers">
              <th>
                <div
                  class="column"
                  :class="[
                    {
                      selected:
                        !isClicked && columnSelected === 'title' && currentSortDir === 'desc'
                    }
                  ]"
                  @click="toggle('title')"
                >
                  <sc-text
                    v-if="fields && fields.column1"
                    :field="fields.column1"
                    class="largeHeadline"
                  />
                </div>
              </th>
              <th>
                <div
                  class="column"
                  :class="[{ selected: isClicked && columnSelected === 'availabilityCost' }]"
                  @click="toggle('availabilityCost')"
                >
                  <sc-text
                    v-if="fields && fields.column2"
                    :field="fields.column2"
                    class="largeHeadline"
                  />
                </div>
              </th>
              <th>
                <div
                  class="column"
                  :class="[{ selected: isClicked && columnSelected === 'secureYourOrganization' }]"
                  @click="toggle('secureYourOrganization')"
                >
                  <sc-text
                    v-if="fields && fields.column3"
                    :field="fields.column3"
                    class="smallHeadline"
                  />
                </div>
              </th>
              <th>
                <div
                  class="column"
                  :class="[{ selected: isClicked && columnSelected === 'secureYourPlatforms' }]"
                  @click="toggle('secureYourPlatforms')"
                >
                  <sc-text
                    v-if="fields && fields.column4"
                    :field="fields.column4"
                    class="smallHeadline"
                  />
                </div>
              </th>
              <th>
                <div
                  class="column"
                  :class="[{ selected: isClicked && columnSelected === 'trackSpecificThreats' }]"
                  @click="toggle('trackSpecificThreats')"
                >
                  <sc-text
                    v-if="fields && fields.column5"
                    :field="fields.column5"
                    class="smallHeadline"
                  />
                </div>
              </th>
            </tr>
            <tr v-for="item in products" :key="item.id">
              <td>
                <h4 v-if="item.title?.value">{{ item.title?.value }}</h4>
                <p v-if="item.description?.value">{{ item.description?.value }}</p>
                <a
                  v-if="item.cta?.url && item.cta?.text"
                  :href="item.cta.url"
                  :target="item.cta.target"
                  class="c-cta--secondary"
                >
                  {{ item.cta.text }}
                </a>
              </td>
              <td>
                <img
                  class="icon"
                  v-if="item.availabilityCost?.targetItem?.image?.src"
                  :src="item.availabilityCost?.targetItem?.image?.src"
                  :alt="item.availabilityCost?.targetItem?.image?.alt"
                />
                <p v-if="item.availabilityCost?.targetItem?.label?.value" class="cost">
                  {{ item.availabilityCost?.targetItem?.label?.value }}
                </p>
                <p v-if="item.availabilityMembership?.value === '1'" class="membership">
                  Membership required
                </p>
                <a
                  v-if="item.availabilityDetails?.value"
                  class="tooltip"
                  :tooltipDetails="`${item.availabilityDetails?.value}`"
                >
                  <span>DETAILS</span>
                </a>
              </td>
              <td>
                <div v-if="item.secureYourOrganization?.value === '1'" class="checkmark"></div>
              </td>
              <td>
                <div v-if="item.secureYourPlatforms?.value === '1'" class="checkmark"></div>
              </td>
              <td>
                <div v-if="item.trackSpecificThreats?.value === '1'" class="checkmark"></div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div v-if="!isLoading" class="button-top">
      <button type="button" @click="backToTop($event)">Go Back To The Top</button>
    </div>
  </div>
</template>

<script>
import { reactive } from 'vue';
import { ItemQuery } from './CIS-Cybersecurity-Tools.graphql';
import { useQuery } from '@vue/apollo-composable';
import { Text } from '@sitecore-jss/sitecore-jss-vue';
import { useCookies } from 'vue3-cookies';
import tippy from 'tippy.js';
import { hideAll } from 'tippy.js';
import 'tippy.js/dist/tippy.css'; // optional for styling

export default {
  name: 'CIS-Cybersecurity-Tools',
  components: {
    ScText: Text
  },
  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },
  data: function () {
    return {
      columnSelected: 'title',
      currentSortDir: 'asc',
      currentSort: 'title',
      isClicked: false
    };
  },
  setup(props) {
    if (!props.fields?.source?.name) {
      throw Error("The CIS-Cybercurity-Tool's Source field is not set.");
    }

    const variables = {
      cybersecurityPath: `/sitecore/content/cisecurity/ngen/Content/${props.fields.source?.name}`
    };

    const { result, loading, error, onResult } = useQuery(ItemQuery, variables);

    const products = reactive([]);

    const { cookies } = useCookies();

    onResult((queryResult) => {
      if (!queryResult.loading && queryResult.data?.items?.children) {
        products.push(...queryResult.data?.items?.children);
        products.sort((a, b) => {
          return a['title'].value.localeCompare(b['title'].value);
        });
      }
    });

    return {
      products,
      result,
      loading,
      error,
      cookies,
      onResult
    };
  },
  computed: {
    isLoading() {
      return this.loading;
    }
  },
  methods: {
    toggle(column) {
      this.columnSelected = column;
      this.isClicked = false;

      if (this.currentSort === column) {
        if (this.currentSortDir === 'asc') this.currentSortDir = 'desc';
        else this.currentSortDir = 'asc';

        if (this.currentSortDir === 'asc') this.isClicked = true;
      } else {
        if (column === 'title') this.currentSortDir = 'asc';
        else this.currentSortDir = 'desc';
      }

      this.products
        .sort((a, b) => {
          return a['title'].value.localeCompare(b['title'].value);
        })
        .sort((a, b) => {
          var af = a['availabilityCost']?.targetItem?.sort?.value;
          var bf = b['availabilityCost']?.targetItem?.sort?.value;
          var as = a['availabilityMembership']?.value;
          var bs = b['availabilityMembership']?.value;

          if (column === 'availabilityCost') {
            if (this.currentSortDir === 'asc') {
              if (af == bf) {
                return as < bs ? 1 : as > bs ? -1 : 0;
              } else {
                return af < bf ? 1 : -1;
              }
            } else {
              if (af == bf) {
                return as < bs ? -1 : as > bs ? 1 : 0;
              } else {
                return af < bf ? -1 : 1;
              }
            }
          } else {
            if (this.currentSortDir === 'asc') {
              return a[column]?.value?.localeCompare(b[column]?.value);
            } else {
              return b[column]?.value?.localeCompare(a[column]?.value);
            }
          }
        });

      this.currentSort = column;

      this.backToTop();
    },
    backToTop() {
      var label = document.querySelector('.table-label');
      var mobileInstruction = document.querySelector('.mobile-instruction');
      var bannerItem = document.querySelector('.c-banner');
      var instruction = 0;
      var elementTopPosition = 0;

      if (label && mobileInstruction) {
        instruction = label.offsetHeight + mobileInstruction.offsetHeight;
      }

      if (bannerItem) {
        if (window.scrollY === 0) {
          elementTopPosition =
            this.$refs.ctElement.offsetTop - bannerItem.offsetHeight - instruction;
        } else {
          elementTopPosition = this.$refs.ctElement.offsetTop - instruction;
        }
      } else {
        elementTopPosition = this.$refs.ctElement.offsetTop;
      }

      // Scroll page to start on cn-container
      if (this.$refs.ctElement) {
        window.scrollTo({
          top: elementTopPosition,
          behavior: 'smooth'
        });
      }

      // Scroll the list to start
      if (this.$refs.scElement) {
        this.$refs.scElement.scroll({ top: 0, behavior: 'smooth' });
      }
    }
  },
  updated() {
    var banner = this.cookies.get('banner');
    var bannerItem = document.querySelector('.c-banner');
    var column = document.querySelectorAll('.column-headers th');

    if (banner === null || banner != 'closed') {
      if (column && bannerItem) {
        var top = bannerItem.offsetHeight - 3;
        column.forEach((th) => (th.style.top = top + 'px'));
      }
    }

    tippy('[tooltipDetails]', {
      content: (reference) =>
        reference.getAttribute('tooltipDetails') + '<div class="tooltip-close">X</div>',
      allowHTML: true,
      interactive: true,
      interactiveDebounce: 75,
      maxWidth: 400,
      placement: 'top-end',
      touch: true,
      onCreate(instance) {
        instance.popper.querySelector('.tooltip-close').onclick = () => {
          instance.hide();
        };
      },
      onShow() {
        hideAll();
      }
    });
  }
};
</script>
<style lang="scss" scoped>
#ct-container {
  padding-bottom: 4rem;
}
.c-cybersecurity-tools {
  position: relative;
  padding: 0;
  margin-top: 5rem;

  @include tablet-up {
    padding: 0 2rem 0 2rem;
    margin-top: 4rem;
  }

  @include large-desktop-up {
    max-width: 144rem;
    margin: 0 auto;
    margin-top: 4rem;
    padding: 0 6rem 0 6rem;
  }

  @include tablet-down {
    &:after {
      content: '';
      position: absolute;
      z-index: 3;
      top: 0;
      right: 0;
      pointer-events: none;
      background-image: linear-gradient(
        to right,
        rgba(255, 255, 255, 0),
        rgba(255, 255, 255, 1) 90%
      );
      width: 2em;
      height: 100%;
    }
  }

  .mobile-instruction {
    text-align: right;
    position: relative;
    z-index: 4;
    @include font-styles('Arial Narrow', 1.6rem, 600, normal, 2rem, 0, $blue);
    margin-right: 0.5rem;

    @include tablet-up {
      display: none;
    }

    span {
      display: inline-block;
      position: relative;
      padding: 0 2.5rem 1rem 0;

      &:after,
      &:before {
        content: '';
        position: absolute;
        top: 20%;
        right: 0;
        display: block;
        width: 1.4rem;
        height: 0.8rem;
        background-image: url(~@/assets/images/icon-chevron.svg);
        background-repeat: no-repeat;
        background-size: contain;
        transform: rotate(-90deg);
      }

      &:after {
        right: 0.7rem;
      }
    }
  }
  .table-label {
    display: none;

    @include tablet-up {
      display: block;
      width: 100%;
      text-align: right;
      position: relative;
      top: 0.1rem;
    }

    span {
      position: relative;
      display: inline-block;
      background-color: $orange;
      border-radius: 0.5rem 0.5rem 0 0;
      padding: 1.1rem 5rem 0.5rem 5rem;
      text-transform: uppercase;
      text-align: center;
      @include font-styles('Arial', 1.4rem, 500, normal, 1.7rem, 0.1rem, $white);

      @include tablet-up {
        width: calc(((100% - 31.3%) / 4) * 3);
      }
    }
  }

  table {
    width: 100%;
    margin: 0 0 1.5rem 0;

    tr {
      &:nth-child(2) {
        td {
          padding-top: 4rem;
        }
      }

      &:last-child {
        td {
          border-bottom: dashed 0.15rem $divider;
        }
      }
    }

    th {
      text-align: center;
      background-color: $box-gray;
      border-left: dashed 0.15rem $divider;
      padding: 1.9rem 1.6rem 1.5rem 1.6rem;
      position: sticky;
      top: -0.1rem;
      z-index: 2;

      @include tablet-down {
        top: -0.1rem !important;
      }

      &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 0.4rem;
        width: calc(100% + 0.1rem);
        background-color: $orange;
      }

      &:first-child {
        @include desktop-down {
          position: sticky;
          left: 0;
          z-index: 3;
          width: 22rem;
        }
      }

      .column {
        position: relative;
        display: flex;
        min-width: 16rem;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        padding-right: 2rem;

        @include tablet-up {
          min-width: auto;
        }

        &.selected {
          &:after {
            transform: translate(-0.2rem, -0.2rem) rotate(180deg);
          }
        }

        &:after {
          content: '';
          position: absolute;
          right: 0;
          display: block;
          width: 1.4rem;
          height: 0.8rem;
          background-image: url(~@/assets/images/icon-chevron.svg);
          background-repeat: no-repeat;
          background-size: contain;
        }
      }

      .largeHeadline {
        @include font-styles('Arial', 2rem, 200, normal, 2.4rem, 0, $blue);

        @include desktop-up {
          @include font-styles('Arial', 2.8rem, 200, normal, 3.5rem, 0, $blue);
        }

        @include desktop-down {
          position: -webkit-sticky;
          position: sticky;
          font-size: 2rem;
          line-height: 2.6rem;
          background: none;
          -webkit-text-fill-color: initial;
        }
      }

      .smallHeadline {
        @include font-styles('Arial', 1.6rem, 500, normal, 2.2rem, 0, $dark-blue);

        @include desktop-up {
          @include font-styles('Arial', 2rem, 500, normal, 2.6rem, 0, $dark-blue);
        }
      }

      &:last-child {
        border-right: dashed 0.15rem $divider;
      }
    }

    td {
      border-left: dashed 0.15rem $divider;
      width: calc((100% - 31.3%) / 4);
      padding: 1.5rem 1.5rem;
      text-align: center;

      @include desktop-down {
        width: 10rem;
        padding: 1.5rem 1rem;
      }

      &:last-child {
        border-right: dashed 0.15rem $divider;
      }

      img {
        width: 2rem;
        height: 2rem;
      }

      &:first-child {
        width: 31.3%;
        padding: 2rem 2.4rem 2rem 1.6rem;
        text-align: left;
        border-left: dashed 0.15rem $divider;
        @include font-styles('Arial', 2rem, 200, normal, 2.6rem, 0, $dark-blue);

        @include desktop-down {
          position: sticky;
          left: -0.2rem;
          z-index: 2;
          width: 22rem;
          background-color: $white !important;
          font-size: 1.4rem;
          line-height: 2rem;
          padding: 1.5rem 1rem 1.5rem 1rem;
        }
      }
    }
  }

  p {
    @include desktop-down {
      font-size: 1.6rem;
      line-height: 2.2rem;
    }
  }

  h4 {
    @include font-styles('Arial', 1.8rem, 200, normal, 2.4rem, 0, $dark-blue);

    @include desktop-up {
      font-size: 2.4rem;
      line-height: 3rem;
    }
  }

  .cost {
    @include font-styles('Arial', 1.4rem, 500, normal, 1.8rem, 0, $dark-blue);
    margin: 0;

    @include desktop-up {
      font-size: 1.8rem;
      line-height: 2.4rem;
      font-weight: 200;
    }
  }

  .icon {
    display: inline-block;
    width: 3.4rem;
    height: 3.4rem;
    margin-bottom: 0.5rem;
  }

  .membership {
    @include font-styles('Arial', 1.4rem, 500, normal, 1.8rem, 0, $dark-blue);
    margin-bottom: 1rem;

    @include desktop-up {
      font-size: 1.8rem;
      line-height: 2.4rem;
      font-weight: 200;
    }
  }

  .c-cta--secondary {
    margin-bottom: 0;
  }

  .checkmark {
    display: inline-block;
    width: 2.1rem;
    height: 1.5rem;
    -webkit-mask: url(~@/assets/images/icon-checkmark.svg);
    mask: url(~@/assets/images/icon-checkmark.svg);
    background-color: $orange;
    mask-repeat: no-repeat;
    background-size: contain;
  }

  .tooltip {
    @include font-styles('Arial Narrow', 1.4rem, 600, normal, 1.7rem, 0, $dark-blue);

    &:after {
      right: -0.2rem;
      bottom: -0.3rem;

      @include tablet-up {
        bottom: -0.7rem;
      }
    }
  }

  &:deep() {
    .scrollable {
      @include tablet-down {
        max-width: 100vw;
        max-height: 100vh;
        overflow-x: scroll;
        position: relative;
      }
    }
    .tippy-content {
      text-align: left;
      padding-right: 1rem;
    }

    .tippy-box[data-placement^='top-end'] {
      top: -1rem;

      @include tablet-up {
        left: 2.5rem;
      }
    }

    .tippy-box[data-placement^='bottom-end'] {
      top: 1rem;

      @include tablet-up {
        left: 2.5rem;
      }
    }
  }
}

.button-top {
  text-align: right;
  margin-right: 0.5rem;

  @include tablet-up {
    padding: 0 2rem;
    margin-right: 0;
  }

  @include large-desktop-up {
    max-width: 144rem;
    margin: 0 auto;
    padding: 0 6rem;
  }

  button {
    background: transparent;
    border: none;
    text-transform: uppercase;
    @include font-styles('Arial Narrow', 1.8rem, 600, normal, 2.2rem, 0, $blue);
    display: inline-block;
    padding: 0;
    margin-top: 1rem;
  }
}
</style>
